/**
 * @author Muhammad Hamza Khan
 * @datetime July, 20th 2022
 * @description: This is EditAccespointDetails Component that is responsible for edit Accesspoint from floors.
 */

import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { ConfirmDialogComponent } from 'src/app/shared-layout/Components/confirm-dialog/confirm-dialog.component';
import { PageName } from 'src/app/shared/utils/user-roles';
import { LoaderService } from 'src/app/shared/utils/loader.service';
import { FloorManagementService } from 'src/app/floor-management/floor-management.service';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { IAccessPoint } from 'src/app/building-managment/building-unit-details/activity/access-point-interfaces';
import { MFValidators } from 'src/app/shared/validators/mf-validators';
import { CommonService } from 'src/app/shared/utils/common.service';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-edit-accesspoint-details',
  templateUrl: './edit-accesspoint-details.component.html',
  styleUrls: ['./edit-accesspoint-details.component.scss'],
})
export class EditAccesspointDetailsComponent implements OnInit {
  editAccesPointForm: any;
  accessPoint: IAccessPoint;
  previousAccessPointName: string = '';
  errorMessage?: string;
  PageName = PageName;
  siteId!: string;
  buildingId!: string;
  floorId!: string;
  readonly CONSTANTS = CONSTANTS;
  readonly LANG_CONST = LANG_CONST;
  sharedData: any;
  buildingName?: string;

  constructor(
    public dialogRef: MatDialogRef<EditAccesspointDetailsComponent>,
    @Inject(MAT_DIALOG_DATA) data: any,
    private helperService: HelperService,
    private loaderService: LoaderService,
    private floorManagementService: FloorManagementService,
    private sharedDataService: SharedDataService,
    private notificationService: NotificationService,
    public commonService: CommonService,
    private translate: TranslateService
  ) {
    this.accessPoint = data?.accessPoint;
    this.siteId = data?.siteId;
    this.buildingId = data?.buildingId;
    this.floorId = data?.floorId;
    this.errorMessage = undefined;
    this.buildingName = data?.buildingName;
  }

  /**
   * @description: runs on when component is initialized.
   * @input {none}
   * @output {void}
   */

  ngOnInit(): void {
    this.editAccesPointForm = new FormGroup({
      accessPointName: new FormControl(null, [
        MFValidators.required,
        MFValidators.minLength(1),
        MFValidators.maxLength(50),
        MFValidators.whitespace,
        MFValidators.requiredSpecialCharForLock,
      ]),
    });
    this.editAccesPointForm.patchValue({
      accessPointName: this.accessPoint?.accessPointName,
    });

    this.previousAccessPointName = this.accessPoint?.accessPointName;
    this.editAccesPointForm?.controls?.accessPointName?.markAsTouched();
  }

  /**
   * @description: ##closeDialog function close the dialogRef.
   * @input none
   * @output { void }
   */
  closeDialog() {
    let accessPointName: string =
      this.editAccesPointForm?.controls?.accessPointName?.value;

    let currentAccesPointName = this.previousAccessPointName;

    if (accessPointName === currentAccesPointName) {
      this.dialogRef?.close();
      return;
    }

    const dialogInstance = this.helperService.openModalDialog(
      ConfirmDialogComponent,
      {
        title: this.translate.instant('TITLE'),
        headingText: this.translate.instant('HEADING_TEXT'),
        bodyText: this.translate.instant('BODY_TEXT'),
        footerText: this.translate.instant('FOOTER_TEXT'),
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
    dialogInstance.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.dialogRef?.close();
      }
    });
  }

  /**
   * @description editAccessPointHandler function accepts siteId , buildingId , floorId , accessPointId and call the updateAccessPoint API from floorManagementService.
   * @param { string } siteId
   * @param { string } buildingId
   * @param { string } floorId
   * @param { string } accessPointId
   * @param { string } accessPointName
   * @returns
   */
  editAccessPointHandler(
    siteId: string,
    buildingId: string,
    floorId: string,
    accessPointId: string,
    accessPointName: string
  ): void {
    accessPointName = accessPointName.toString();
    if (
      accessPointName != this.sharedData?.selectedAccessPoint?.accessPointName
    ) {
      this.floorManagementService
        .updateAccessPoint(
          siteId,
          buildingId,
          floorId,
          accessPointId,
          accessPointName
        )
        .subscribe({
          next: (res: any) => {
            this.sharedDataService.setSelectedAccessPoint({
              accessPointId: accessPointId,
              accessPointName: accessPointName,
            });
            this.accessPoint.accessPointName =
              this.editAccesPointForm?.controls?.accessPointName?.value;
            this.floorManagementService?.setTriggerRerender(true);
            this.dialogRef?.close(true);
            this.loaderService?.show();
            this.notificationService.showSuccess(
              this.translate.instant('SUCCESSFULLY_UPDATED')
            );
          },
          error: (err: Error) => {
            this.errorMessage = err?.message;
            this.notificationService.showError(err?.message);
          },
        });
    } else this.dialogRef.close(false);
  }

  /**
   * @description this method removes error message displayed on the top
   */
  closeError(): void {
    this.errorMessage = undefined;
  }
}
